import {
  CalendarDate,
  DateValue,
  getLocalTimeZone,
  today,
} from "@internationalized/date"
import { keepPreviousData, useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { BookingCalendarDateAvailability } from "ipctg-inflow-types/InFlowDB"
import { ReservationDateAvailabilityStatus } from "ipctg-inflow-types/InFlowEnums"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import PendingComponent from "@/globalComponents/atoms/PendingComponent"
import Text from "@/globalComponents/atoms/Text"
import Calendar, {
  type CalendarDateStatus,
} from "@/globalComponents/molecules/Calendar"
import DatePicker from "@/globalComponents/molecules/DatePicker"
import { getRouteParamsForDate } from "@/utilities/date"
import {
  calendarAvailabilityQueryOptions,
  calendarsQueryOptions,
} from "@/utilities/hooks/queries/reserve"
import { useTheme } from "@/utilities/hooks/useTheme"

import { CheckBackModal } from "./-components/CheckBackModal"
import { NotRequiredModal } from "./-components/NotRequiredModal"
import { SoldOutModal } from "./-components/SoldOutModal"
import styles from "./index.module.css"

export const Route = createFileRoute(
  "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/"
)({
  staticData: {
    pageName: "Calendar",
  },
  component: RouteComponent,
})

function toCalendarStatus(
  type: ReservationDateAvailabilityStatus
): CalendarDateStatus {
  switch (type) {
    case ReservationDateAvailabilityStatus.Available:
      return "selectable"
    case ReservationDateAvailabilityStatus.NotRequired:
      return "notRequired"
    case ReservationDateAvailabilityStatus.Full:
    case ReservationDateAvailabilityStatus.FullButNotAllConfirmed:
      return "blocked"
    case ReservationDateAvailabilityStatus.ComingSoon:
      return "disabled"
    case ReservationDateAvailabilityStatus.NoInventoryActive:
    case ReservationDateAvailabilityStatus.Closed:
      return "unavailable"
  }
}

function buildStatusMap(
  availability: Record<string, BookingCalendarDateAvailability>
): Map<string, CalendarDateStatus> {
  return new Map(
    Object.entries(availability).map(([date, avail]) => [
      date,
      toCalendarStatus(avail.availabilityStatus),
    ])
  )
}

function RouteComponent() {
  const navigate = Route.useNavigate()
  const params = Route.useParams()
  const { t } = useTranslation("reserve")
  const { scannedArea } = useTheme()
  const timezoneLocation = scannedArea!.theme.locationInformation.timeZone
  const locationID = scannedArea!.theme.locationInformation.locationID
  const { data: calendars } = useSuspenseQuery(
    calendarsQueryOptions({ locationID })
  )
  const calendarDescription = calendars.find(
    (c) => c.bookingCalendarID === params.calendarID
  )?.metadata.reservationDatesDescription

  const [selectedDate, setSelectedDate] = useState<DateValue | null>(null)
  const focusedDate = useMemo(
    () => new CalendarDate(Number(params.year), Number(params.month), 1),
    [params.year, params.month]
  )
  const [dateError, setDateError] = useState<string | undefined>()
  const [openModal, setOpenModal] = useState<
    "checkBack" | "soldOut" | "notRequired" | null
  >(null)

  const todayDate = today(timezoneLocation || getLocalTimeZone())
  const currentMonth = `${params.year}-${params.month.padStart(2, "0")}`

  const { data: availability, isFetching } = useQuery({
    ...calendarAvailabilityQueryOptions({
      bookingCalendarID: params.calendarID,
      month: currentMonth,
    }),
    placeholderData: keepPreviousData,
  })
  const statusMap = buildStatusMap(availability ?? {})

  function navigateToMonth(year: number, month: number, replace = false) {
    navigate({
      to: "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month",
      params: {
        ...params,
        year: String(year),
        month: String(month).padStart(2, "0"),
      },
      replace,
    })
  }

  return (
    <>
      <Heading level={1} as={1} className="center">
        {t("calendar.heading")}
      </Heading>
      <div className={styles.infoSection}>
        {calendarDescription && <Text level={1}>{calendarDescription}</Text>}
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Text level={2} className="semi-bold">
            {t("calendar.selectDate")}
          </Text>
          <Button
            buttonType="primary"
            presentation="link"
            style={{ fontSize: "var(--font-size-2)", fontWeight: 400 }}
            onPress={() => {
              setSelectedDate(todayDate)
              const sameMonth =
                String(todayDate.year) === params.year &&
                String(todayDate.month).padStart(2, "0") === params.month
              if (!sameMonth) {
                navigateToMonth(todayDate.year, todayDate.month, true)
              }
            }}
          >
            {t("calendar.today")}
          </Button>
        </div>
        <DatePicker value={selectedDate} onChange={handleDateChange} isReadOnly>
          <DatePicker.DateField isReadOnly />
        </DatePicker>
        <FieldError>{dateError}</FieldError>
      </div>
      <div className={styles.calendarWrapper}>
        <Card className={styles.calendarCard}>
          <Calendar
            value={selectedDate}
            focusedValue={focusedDate}
            onFocusChange={(date) => {
              const sameMonth =
                String(date.year) === params.year &&
                String(date.month).padStart(2, "0") === params.month
              if (!sameMonth) {
                navigateToMonth(date.year, date.month, true)
              }
            }}
            onChange={handleDateChange}
            minValue={todayDate}
            statusMap={statusMap}
          />
        </Card>
        {isFetching && (
          <>
            <div className={styles.calendarOverlay} />
            <div className={styles.calendarSpinner}>
              <PendingComponent variant="block" />
            </div>
          </>
        )}
      </div>
      <Button
        buttonType="primary"
        onPress={() => {
          if (!selectedDate) {
            setDateError(t("calendar.selectDateRequired"))
            return
          }
          navigate({
            to: "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/rates",
            params: {
              ...params,
              ...getRouteParamsForDate(selectedDate),
            },
          })
        }}
      >
        {t("calendar.next")}
      </Button>

      <CheckBackModal
        isOpen={openModal === "checkBack"}
        onOpenChange={(open) => !open && setOpenModal(null)}
      />
      <SoldOutModal
        isOpen={openModal === "soldOut"}
        onOpenChange={(open) => !open && setOpenModal(null)}
      />
      <NotRequiredModal
        isOpen={openModal === "notRequired"}
        onOpenChange={(open) => !open && setOpenModal(null)}
      />
    </>
  )

  function handleDateChange(date: DateValue | null) {
    if (date) {
      const status = statusMap.get(date.toString())
      if (status === "disabled") {
        setOpenModal("checkBack")
        return
      }
      if (status === "blocked") {
        setOpenModal("soldOut")
        return
      }
      if (status === "notRequired") {
        setOpenModal("notRequired")
        return
      }
    }
    setSelectedDate(date)
    setDateError(undefined)
  }
}
